"use strict";

import GeometryCollection from "ol/geom/GeometryCollection";
import Geometry from 'ol/geom/Geometry';
import Feature from "ol/Feature";
import WKT from "ol/format/WKT";
import GeoJSON from "ol/format/GeoJSON";

/**
 * @classdesc
 * Reader-/Writer-Implementierung auf Basis der Openlayers Kartenbibliothek.
 *
 * @class
 * @extends gc.gis.GCWrappable
 * @memberof gc.gis.format
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.format.OLFormat = function()
{
    gc.gis.format.GCFormat.call(this);

	/**
	 * @type {gc.gis.geom.OLGeometryFactory}
	 * @private
	 */
	this._geometryFactory = new gc.gis.geom.OLGeometryFactory();
};
gc.gis.format.OLFormat.prototype = Object.create(gc.gis.format.GCFormat.prototype);

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.readGeometry = function(geometry, format, opt_crs)
{
    let geom;
    const ol_geom = this._parseGeometry(geometry, format);

    if(!ol_geom)
    {
        throw new gc.gis.error.GCError('Invalid geometry.');
    }

    geom = this._geometryFactory.createGeometry(ol_geom);
    if (opt_crs)
    {
        geom.setCRS(opt_crs);
    }

    return geom;
};

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.readGeometryList = function(geometries, format, opt_crs)
{
    if (!Array.isArray(geometries))
    {
        return this.readGeometry(geometries, format, opt_crs);
    }
    else if (geometries.length === 1)
    {
        return this.readGeometry(geometries[0], format, opt_crs);
    }

    let geom;
    let ol_geom = null;
    const ol_geometries = [];

    for (let i=0; i<geometries.length; i++)
    {
        ol_geom = this._parseGeometry(geometries[i], format);

        if (!ol_geom)
        {
            throw new gc.gis.error.GCError('Invalid geometry.');
        }

        ol_geometries.push(ol_geom);
    }

    geom = this._geometryFactory.createGeometryCollection(new GeometryCollection(ol_geometries));
    if (opt_crs)
    {
        geom.setCRS(opt_crs);
    }

    return geom;
};

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.writeGeometry  = function (geometry, format)
{
	let ol_geom = null;
	const ol_format = this._createFormat(format);

	if (geometry instanceof gc.gis.geom.GCGeometry)
	{
		ol_geom = geometry.getImpl();
	}
	else if (geometry instanceof Geometry)
	{
		ol_geom = geometry;
	}

	// Geometry serialisieren
	const result = ol_format.writeGeometry(ol_geom);
	if (!result)
	{
		throw new gc.gis.error.GCError('Failed to serialize geometry.');
	}

	return result;
};

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.writeGeometryObject  = function (geometry)
{
	let ol_geometry = null;
	const ol_format = this._createFormat(gc.gis.format.GEOJSON);
	const ol_options = {};

	if (geometry instanceof gc.gis.geom.GCGeometry)
	{
		ol_geometry = geometry.getImpl();

		if (geometry.getCRS())
		{
			ol_options.dataProjection = geometry.getCRS();
		}
	}
	else if (geometry instanceof Geometry)
	{
		ol_geometry = geometry;
	}

	// Geometry serialisieren
	const result = ol_format.writeGeometryObject(ol_geometry, ol_options);

	// TODO: serialize dataProjection as crs member when writing (in ol/format/geojson.js) Stand: Version 4.5
	// CRS Member ergänzen, da nicht durch OL realisiert
	if (geometry.getCRS())
	{
		result.crs = {
			type: 'name',
			properties: {
				name: geometry.getCRS()
			}
		};
	}

	if (!result)
	{
		throw new gc.gis.error.GCError('Failed to serialize feature.');
	}

	return result;
};

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.writeFeature = function (feature, format)
{
	let ol_feature = null;
	const ol_format = this._createFormat(format);

	if (feature instanceof gc.gis.feature.GCFeature)
	{
		ol_feature = feature.getImpl();
	}
	else if (feature instanceof Feature)
	{
		ol_feature = feature;
	}

	// Feature serialisieren
	const result = ol_format.writeFeature(ol_feature);
	if (!result)
	{
		throw new gc.gis.error.GCError('Failed to serialize feature.');
	}

	return result;
};

/**
 * @inheritDoc
 */
gc.gis.format.OLFormat.prototype.writeFeatureObject = function (feature)
{
	let ol_feature = null;
	const ol_format = this._createFormat(gc.gis.format.GEOJSON);
	const ol_options = {};

	if (feature instanceof gc.gis.feature.GCFeature)
	{
		ol_feature = feature.getImpl();

		if (feature.getGeometry().getCRS())
		{
			ol_options.dataProjection = feature.getGeometry().getCRS();
		}
	}
	else if (feature instanceof Feature)
	{
		ol_feature = feature;
	}

	// Feature serialisieren
	const result = ol_format.writeFeatureObject(ol_feature, ol_options);

	// TODO: serialize dataProjection as crs member when writing (in ol/format/geojson.js) Stand: Version 4.5
	// CRS Member ergänzen, da nicht durch OL realisiert
	if (feature.getGeometry().getCRS())
	{
		result.crs = {
			type: 'name',
			properties: {
				name: feature.getGeometry().getCRS()
			}
		};
	}

	if (!result)
	{
		throw new gc.gis.error.GCError('Failed to serialize feature.');
	}

	return result;
};

/**
 * Überführt die übergebene Geometrie in ein Openlayers-Geometrieobjekt.
 *
 * @param {*} geometry Geometrie
 * @param {string} format Formatstring der Geometrie
 * @return {ol.geom.Geometry|null}
 * @private
 * @throws {gc.gis.error.GCError} sofern die Geometrie nicht verarbeitet werden kann
 */
gc.gis.format.OLFormat.prototype._parseGeometry = function(geometry, format)
{
    let ol_format = undefined;
    let ol_geom = null;

    if (geometry instanceof gc.gis.geom.GCGeometry)
    {
        ol_geom = geometry.getImpl();
    }
    else if (geometry instanceof Geometry)
    {
        ol_geom = geometry;
    }
    else
    {
        ol_format = this._createFormat(format);

	    ol_geom = ol_format.readGeometry(geometry);
	    if (!ol_geom)
	    {
		    throw new gc.gis.error.GCError('Failed to parse geometry: ' + geometry);
	    }
    }

    return ol_geom;
};

/**
 * @param {string} format
 * @return {ol.format.Feature}
 * @private
 * @throws {gc.gis.error.GCError} sofern keine Formater erzeugt werden kann
 */
gc.gis.format.OLFormat.prototype._createFormat = function (format)
{
    let ol_format = null;

    // WKT
	if(format === gc.gis.format.WKT)
	{
		ol_format = new WKT();
	}
	// GeoJSON
	else if(format === gc.gis.format.GEOJSON)
	{
		ol_format = new GeoJSON();
	}

	if (!ol_format)
    {
	    throw new gc.gis.error.GCError('Format not recognized: ' + format);
    }

	return ol_format;
};